import { IonInputCustomEvent } from '@ionic/core';
import { IonCol, IonContent, IonGrid, IonItem, IonList, IonPage, IonRadioGroup, IonRow, IonText } from '@ionic/react';
import * as React from 'react';
import { useEffect, useState } from 'react';

import styles from '@/pages/Upload/Upload.module.css';

import { Button } from '@/components/Button';
import { UploadArea } from '@/components/FileInput/uploadArea';
import { HighlightAlinea } from '@/components/HighlightAlinea/HighlightAlinea';
import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Radio } from '@/components/Radio';
import { Tag } from '@/components/Tag/Tag';
import { TagModal } from '@/components/TagModal/TagModal';
import { toast } from '@/components/Toaster/Toast';

import { FileData, reportOption, useFirebaseUpload } from '@/hooks/useFirebaseUpload';

import { useAuthContext } from '@/contexts/authContext';

import type { ITag } from '@/validations/tag';

/** Kinematic data upload page
 * Using the useFirebaseUpload hook to handle the actual upload to cloud storage
 */
export const UploadPage = () => {
  // setting up the hook to upload file and track its progress
  const { dataResponse, isLoading, isError, errorMessage, progress, setUploadData, clearError } = useFirebaseUpload();
  const user = useAuthContext().state.userState?.user;
  const [userConfirmedReportType, setUserConfirmedReportType] = useState<reportOption>('undefined');
  const [disabled, setDisabled] = useState(true);
  const [detectedOption, setDetectedOption] = useState('');

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [option, setOption] = useState<reportOption>('undefined');

  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const [file, setFile] = useState<FileData>();

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        title: errorMessage,
        duration: 3000,
      });
    }
  }, [isError]);

  function handleFocusEvent(ev: IonInputCustomEvent<FocusEvent>) {
    ev.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleReportType(reportType: reportOption, file: any) {
    setOption(reportType);
    setFile(file);
    setDetectedOption(reportType);
    setDisabled(false);
  }

  function handleUndefined(reportType: reportOption, file: any) {
    setOption(reportType);
    setFile(file);
  }

  const handleFileSelect = (files: FileList) => {
    const startIndex = files[0].name.indexOf('- ') + 2;
    const endIndex = files[0].name.indexOf('.csv');
    const matchArray = files[0].name.match(/\(+\d+\)+/g);
    const index = matchArray ? files[0].name.indexOf(matchArray[0]) : -1;

    if (index < 0) {
      const reportType = files[0].name.substring(startIndex, endIndex);

      if (reportType === 'Walk') handleReportType(reportType, files[0]);
      else if (reportType === 'Run') handleReportType(reportType, files[0]);
      else if (reportType === 'Double Leg Squat') handleReportType('Double Leg Squat', files[0]);
      else if (reportType === 'Single Leg Squat') handleReportType('Single Leg Squat', files[0]);
      else handleUndefined('undefined', files[0]);

      // setOption(e.target.files[0].name.substring(startIndex, endIndex));
    } else {
      const reportType = files[0].name.substring(startIndex, index);

      if (reportType === 'Walk') handleReportType(reportType, files[0]);
      else if (reportType === 'Run') handleReportType(reportType, files[0]);
      else if (reportType === 'Double Leg Squat') handleReportType('Double Leg Squat', files[0]);
      else if (reportType === 'Single Leg Squat') handleReportType('Single Leg Squat', files[0]);
      else handleUndefined('undefined', files[0]);
      // setOption(e.target.files[0].name.substring(startIndex, indexIndex));
    }
    // e.target.files.length > 0 && setFile(e.target.files[0]);
  };

  // Open tag modal for editing
  const openTagModal = () => setTagModalOpen(true);
  const closeTagModal = () => setTagModalOpen(false);

  // Handle tag selection/creation from modal
  const handleTagModalClose = (changedTags?: ITag[]) => {
    closeTagModal();

    // If nothing is changed, return
    if (!changedTags) return;

    const updatedTags = changedTags ?? [];

    // Update local state to reflect the newly changed tags
    setSelectedTags(updatedTags);
  };

  // Remove a tag
  const handleTagDelete = (index: number) => {
    setSelectedTags(selectedTags.filter((_, i) => i !== index));
  };

  const handleOptionChange = (ev: Event) => {
    setUserConfirmedReportType((ev.target as HTMLInputElement).value as reportOption);
    setOption((ev.target as HTMLInputElement).value as reportOption);
    setDisabled(false);
  };

  const handleFileRemove = () => {
    setFile(undefined);
    setOption('undefined');
    setDisabled(true);
  };

  // The actual file upload handler once you click Submit in the form
  const upload = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);

    if (!!user && !!file) {
      const user_id = user.uid;
      // Pass tag IDs to backend
      await setUploadData({ user_id, tags: selectedTags.map((t) => t.id), option, file, USEDSYSTEM: 'GO' });
      setFile(undefined);
      setOption('undefined');
      setUserConfirmedReportType('undefined');
      setDisabled(true);
      handleFileRemove();
      setSelectedTags([]);
    }
  };

  useEffect(() => {
    if (dataResponse || progress?.value === 100) {
      toast({
        variant: 'success',
        title: 'Upload successful',
        description: 'Datafile has been uploaded successfully',
        duration: 6000,
      });
    }
  }, [dataResponse, progress]);

  return (
    <>
      <IonPage>
        <IonContent className='ion-padding' color='light' id='main'>
          <IonGrid className='' fixed style={{ height: '100%', alignContent: 'space-evenly' }}>
            {isLoading && progress && (
              <IonRow className='ion-padding-top ion-margin-top ion-align-items-center ion-justify-content-center'>
                <CustomLoading
                  leadingIcon='document'
                  leadingIconDirection={IconRotate.North}
                  message={'Uploading file...'}
                />
              </IonRow>
            )}

            {!isLoading && !progress && (
              <>
                <IonRow className='ion-justify-content-center ion-margin-vertical'>
                  <HighlightAlinea
                    icon='info'
                    iconColor='#e7364b'
                    title='This upload page is only for files from the blue ORYX GO Gateway'>
                    <>
                      This upload page is only for files from the blue ORYX GO Gateway. <br />
                      The files of the white gateway cannot be uploaded here! <br />
                      If you are unsure which system you have, please contact support.
                    </>
                  </HighlightAlinea>
                </IonRow>
                <form noValidate onSubmit={upload} style={{ height: '75%' }}>
                  <IonGrid className='' fixed style={{ height: '100%', alignContent: 'space-evenly' }}>
                    <IonRow className='ion-padding-top ion-margin-top ion-align-items-center'>
                      <IonCol size='6'>
                        <IonRow>
                          <IonCol size='12'>
                            <UploadArea onChange={handleFileSelect} onFileRemove={handleFileRemove} />
                            {!formSubmitted && file === undefined && !disabled && (
                              <IonText>
                                <p className='ion-padding-start errorText ion-text-center'>Data file is required</p>
                              </IonText>
                            )}
                          </IonCol>
                        </IonRow>
                        <IonRow className=''>
                          <Button onClick={openTagModal} size='small' variant='primary'>
                            Add tags
                          </Button>

                          <div className={styles.tags}>
                            {selectedTags.map((tag, index) => (
                              <Tag key={tag.id} variant='measurement'>
                                {tag.label}

                                <span className={styles.deleteButton} onClick={() => handleTagDelete(index)}>
                                  <Icon name='delete' />
                                </span>
                              </Tag>
                            ))}
                          </div>
                          <TagModal
                            handleCancel={closeTagModal}
                            handleClose={handleTagModalClose}
                            initialValue={selectedTags}
                            isOpen={tagModalOpen}
                          />
                        </IonRow>
                      </IonCol>
                      {file && file !== undefined && (
                        <IonCol size='6'>
                          {option === 'undefined' && (
                            <IonRow className='ion-align-items-center ion-justify-content-center ion-margin-vertical'>
                              <IonText className='ion-text-center'>
                                <p>
                                  We could not detect your reporttype. Please select the correct report type from the
                                  list below.
                                </p>
                              </IonText>
                            </IonRow>
                          )}
                          {option !== 'undefined' && (
                            <>
                              <IonRow className='ion-align-items-center ion-justify-content-center ion-margin-vertical'>
                                <IonText className='ion-text-center'>
                                  <p>
                                    We have detected that you are uploading a{' '}
                                    <strong> {detectedOption ? detectedOption : option} </strong> report. Is this
                                    correct?
                                  </p>
                                </IonText>
                              </IonRow>
                              <IonRow className='ion-align-items-center ion-justify-content-center ion-margin-vertical'>
                                <IonText className='ion-text-center'>
                                  <p>If not please select the correct report type from the list below.</p>
                                </IonText>
                              </IonRow>
                            </>
                          )}
                          <IonRow className='ion-justify-content-center ion-margin-vertical'>
                            <IonList className='' lines='none' style={{ width: '75%', borderRadius: '12px' }}>
                              <IonRadioGroup
                                onIonChange={handleOptionChange}
                                value={userConfirmedReportType != 'undefined' ? userConfirmedReportType : option}>
                                <IonItem>
                                  <Radio value='Walk'>Walk</Radio>
                                </IonItem>
                                <IonItem>
                                  <Radio value='Run'>Run</Radio>
                                </IonItem>
                                <IonItem>
                                  <Radio value='Double Leg Squat'>Double Leg Squat</Radio>
                                </IonItem>
                                <IonItem>
                                  <Radio value='Single Leg Squat'>Single Leg Squat</Radio>
                                </IonItem>
                              </IonRadioGroup>
                            </IonList>
                          </IonRow>
                        </IonCol>
                      )}
                    </IonRow>

                    <IonRow>
                      <IonCol size='6'>
                        <Button disabled={disabled} type='submit'>
                          Submit
                        </Button>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </form>
              </>
            )}
          </IonGrid>
        </IonContent>
      </IonPage>
    </>
  );
};
